import React from 'react';
import { Card, Row, Col, Timeline, Alert, Divider } from 'antd';
import { motion } from 'framer-motion';
import {
  SendOutlined,
  SafetyCertificateOutlined,
  TruckOutlined,
  InfoCircleOutlined,
  BankOutlined,
  InboxOutlined,
  TeamOutlined,
} from '@ant-design/icons';

const cardShell = {
  background: '#ffffff',
  border: '1px solid #d2d2d7',
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
};

const ServiceIntroduction: React.FC = () => {
  return (
    <section
      id="service-intro"
      style={{
        padding: '80px 0',
        background: '#f5f5f7',
        position: 'relative',
      }}
    >
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          style={{ textAlign: 'center', marginBottom: 60 }}
        >
          <h2
            className="display-medium"
            style={{
              marginBottom: 24,
              color: '#1d1d1f',
            }}
          >
            美国空运专线服务解析
          </h2>
          <p
            className="body-large"
            style={{
              color: '#6e6e73',
              maxWidth: 900,
              margin: '0 auto',
              fontSize: '1.125rem',
              lineHeight: 1.6,
            }}
          >
            空运专线是集
            <strong style={{ color: '#1d1d1f', fontWeight: 600 }}>干线运输、目的地清关、关税代付、末端配送</strong>
            于一体的综合物流解决方案。
            <br />
            通过专业的航线资源整合和清关服务网络，为您提供从中国直达美国的
            <strong style={{ color: '#1d1d1f', fontWeight: 600 }}>一站式门到门</strong>
            服务体验。
          </p>
        </motion.div>

        <Row gutter={[32, 32]}>
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <Card style={cardShell} bodyStyle={{ padding: 32 }}>
                <h3
                  style={{
                    textAlign: 'center',
                    marginBottom: 32,
                    color: '#1d1d1f',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}
                >
                  专线服务模式对比
                </h3>

                <Timeline
                  items={[
                    {
                      dot: <SendOutlined style={{ fontSize: 16, color: '#0071e3' }} />,
                      children: (
                        <div>
                          <h4 style={{ color: '#1d1d1f', margin: '0 0 8px 0', fontWeight: 600 }}>
                            空派专线（Express Delivery）
                          </h4>
                          <p style={{ color: '#6e6e73', margin: '0 0 8px 0', fontSize: 14 }}>
                            <strong>模式：</strong>航空运输 + 国际快递配送 (UPS/FedEx)
                          </p>
                          <div
                            style={{
                              background: '#fafafc',
                              padding: '8px 12px',
                              borderRadius: 8,
                              fontSize: 13,
                              color: '#1d1d1f',
                              border: '1px solid #d2d2d7',
                            }}
                          >
                            ✓ 适用：标准包装货物，单件≤70磅
                            <br />
                            ✓ 优势：时效快、跟踪精准、签收便利
                          </div>
                        </div>
                      ),
                    },
                    {
                      dot: <TruckOutlined style={{ fontSize: 16, color: '#0071e3' }} />,
                      children: (
                        <div>
                          <h4 style={{ color: '#1d1d1f', margin: '0 0 8px 0', fontWeight: 600 }}>
                            空卡专线（Truck Delivery）
                          </h4>
                          <p style={{ color: '#6e6e73', margin: '0 0 8px 0', fontSize: 14 }}>
                            <strong>模式：</strong>航空运输 + 卡车派送服务
                          </p>
                          <div
                            style={{
                              background: '#fafafc',
                              padding: '8px 12px',
                              borderRadius: 8,
                              fontSize: 13,
                              color: '#1d1d1f',
                              border: '1px solid #d2d2d7',
                            }}
                          >
                            ✓ 适用：大件货物、托盘、木箱包装
                            <br />
                            ✓ 优势：成本更低、承载能力强
                          </div>
                        </div>
                      ),
                    },
                    {
                      dot: <SafetyCertificateOutlined style={{ fontSize: 16, color: '#0071e3' }} />,
                      children: (
                        <div>
                          <h4 style={{ color: '#1d1d1f', margin: '0 0 8px 0', fontWeight: 600 }}>DDP专线服务</h4>
                          <p style={{ color: '#6e6e73', margin: '0 0 8px 0', fontSize: 14 }}>
                            <strong>全包模式：</strong>Delivered Duty Paid 完税后交货
                          </p>
                          <div
                            style={{
                              background: '#fafafc',
                              padding: '8px 12px',
                              borderRadius: 8,
                              fontSize: 13,
                              color: '#1d1d1f',
                              border: '1px solid #d2d2d7',
                            }}
                          >
                            ✓ 包含：清关、关税、末端配送
                            <br />
                            ✓ 优势：一站式服务、收货方零操作
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />

                <Alert
                  message="专业提示"
                  description="我们提供DDP和DAP两种贸易模式。DDP为完税交货，DAP需收货方自行处理清关和缴税。"
                  type="info"
                  showIcon
                  style={{ marginTop: 24 }}
                  icon={<InfoCircleOutlined />}
                />

                <div
                  style={{
                    marginTop: 24,
                    background: '#fafafc',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #d2d2d7',
                  }}
                >
                  <h4 style={{ color: '#1d1d1f', marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
                    318空派与同行对比
                  </h4>
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#1d1d1f' }}>
                        <strong>318空派：</strong> 5-8天稳定时效
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#6e6e73' }}>
                        <strong>行业标准：</strong> 7-10天时效
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#1d1d1f' }}>
                        <strong>318空派：</strong> 0.5kg起收
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#6e6e73' }}>
                        <strong>行业标准：</strong> 21kg起收
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#1d1d1f' }}>
                        <strong>318空派：</strong> 四大机场清关
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 13, color: '#6e6e73' }}>
                        <strong>一般专线：</strong> 单一清关口岸
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </motion.div>
          </Col>

          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <Card style={cardShell} bodyStyle={{ padding: 32 }}>
                <h3
                  style={{
                    textAlign: 'center',
                    marginBottom: 32,
                    color: '#1d1d1f',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}
                >
                  318空派专线核心优势
                </h3>

                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      background: '#fafafc',
                      padding: 20,
                      borderRadius: 12,
                      border: '1px solid #d2d2d7',
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                          <SendOutlined style={{ fontSize: 28, color: '#0071e3', marginBottom: 8 }} />
                          <div style={{ fontWeight: 600, color: '#1d1d1f', fontSize: 16 }}>空派服务</div>
                          <div style={{ fontSize: 13, color: '#6e6e73', marginTop: 4 }}>Air Express Service</div>
                        </div>
                        <div
                          style={{
                            background: '#ffffff',
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 13,
                            color: '#1d1d1f',
                            lineHeight: 1.5,
                            border: '1px solid #d2d2d7',
                          }}
                        >
                          <div>
                            <strong>航线优势</strong>
                          </div>
                          <div>• CA/MU/AA等主流航司直飞</div>
                          <div>• LAX/SFO/JFK/ORD四大枢纽</div>
                          <div>• 每周3+航班稳定舱位</div>
                          <div>• 国内+香港固定包板资源</div>
                          <div>• 航班频次高，减少延误风险</div>
                        </div>
                      </Col>

                      <Col span={12}>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                          <TruckOutlined style={{ fontSize: 28, color: '#0071e3', marginBottom: 8 }} />
                          <div style={{ fontWeight: 600, color: '#1d1d1f', fontSize: 16 }}>清关服务</div>
                          <div style={{ fontSize: 13, color: '#6e6e73', marginTop: 4 }}>Customs Clearance</div>
                        </div>
                        <div
                          style={{
                            background: '#ffffff',
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 13,
                            color: '#1d1d1f',
                            lineHeight: 1.5,
                            border: '1px solid #d2d2d7',
                          }}
                        >
                          <div>
                            <strong>清关优势</strong>
                          </div>
                          <div>• ISF专业预申报团队</div>
                          <div>• 97.2%清关成功率保障</div>
                          <div>• 12-48小时快速放行</div>
                          <div>• 按邮编分区多点清关</div>
                          <div>• 缩短派送半径提升时效</div>
                        </div>
                      </Col>
                    </Row>

                    <Divider style={{ margin: '20px 0' }} />

                    <div
                      style={{
                        textAlign: 'center',
                        background: '#ffffff',
                        padding: 16,
                        borderRadius: 8,
                        border: '1px solid #d2d2d7',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: '#1d1d1f',
                          marginBottom: 12,
                        }}
                      >
                        专线核心价值
                      </div>
                      <div style={{ fontSize: 14, color: '#6e6e73', lineHeight: 1.8, marginBottom: 12 }}>
                        <strong style={{ color: '#1d1d1f' }}>一票到底</strong> •{' '}
                        <strong style={{ color: '#1d1d1f' }}>全程可控</strong> •{' '}
                        <strong style={{ color: '#1d1d1f' }}>时效稳定</strong> •{' '}
                        <strong style={{ color: '#1d1d1f' }}>价格透明</strong>
                      </div>

                      <div
                        style={{
                          background: '#fafafc',
                          padding: 10,
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#1d1d1f',
                          lineHeight: 1.4,
                          border: '1px solid #d2d2d7',
                        }}
                      >
                        <div>
                          <strong>FBA专车服务</strong>
                        </div>
                        <div>• ONT8/FTW1/SMF3等主要仓库</div>
                        <div>• 固定专车配送，时效更稳定</div>
                      </div>
                    </div>
                  </div>
                </div>

                <Alert
                  message="318空派专线特色"
                  description="专注美国市场10年+，四大机场清关资源，0.5kg起收，当天出单，超时赔付，为您提供最专业的空派服务"
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              </Card>
            </motion.div>
          </Col>
        </Row>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.1 }}
          style={{ marginTop: 60 }}
        >
          <Card style={cardShell} bodyStyle={{ padding: 40 }}>
            <h3
              style={{
                textAlign: 'center',
                marginBottom: 32,
                color: '#1d1d1f',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              服务客户类型
            </h3>

            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <div
                  style={{
                    background: '#ffffff',
                    padding: 24,
                    borderRadius: 12,
                    border: '1px solid #d2d2d7',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <BankOutlined style={{ fontSize: 32, color: '#0071e3', marginBottom: 16 }} />
                  <h4 style={{ color: '#1d1d1f', marginBottom: 12, fontWeight: 600 }}>传统B2B贸易</h4>
                  <p style={{ color: '#6e6e73', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                    工厂出口、贸易公司、批发商等传统外贸企业，需要稳定的国际物流服务支持其业务发展
                  </p>
                </div>
              </Col>

              <Col xs={24} md={8}>
                <div
                  style={{
                    background: '#ffffff',
                    padding: 24,
                    borderRadius: 12,
                    border: '1px solid #d2d2d7',
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <InboxOutlined style={{ fontSize: 32, color: '#0071e3', marginBottom: 16 }} />
                  <h4 style={{ color: '#1d1d1f', marginBottom: 12, fontWeight: 600 }}>跨境电商</h4>
                  <p style={{ color: '#6e6e73', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                    Amazon FBA、独立站、第三方平台卖家，需要快速补货和高效的海外仓储配送
                  </p>
                </div>
              </Col>

              <Col xs={24} md={8}>
                <div
                  style={{
                    background: '#ffffff',
                    padding: 24,
                    border: '1px solid #d2d2d7',
                    borderRadius: 12,
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <TeamOutlined style={{ fontSize: 32, color: '#0071e3', marginBottom: 16 }} />
                  <h4 style={{ color: '#1d1d1f', marginBottom: 12, fontWeight: 600 }}>物流同行</h4>
                  <p style={{ color: '#6e6e73', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                    货代公司、物流企业寻求可靠的专线合作伙伴，扩大服务范围和提升客户满意度
                  </p>
                </div>
              </Col>
            </Row>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceIntroduction;
